import React, {useState, useEffect} from "react";
import {useNavigate} from "react-router-dom";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Button from "@mui/material/Button";
import {
  TableList,
  PickerSearch,
  FormDatePicker,
  Spinner,
} from "../../components";
import CustomizedSnackbars from "../../components/base/Snackbar";
import api from "../../helpers/api";
import {tableHeadTransaction} from "../../utils/Constants";
import DownloadForOfflineIcon from "@mui/icons-material/DownloadForOffline";
import SearchIcon from "@mui/icons-material/Search";
import {exportToXLSX} from "../../helpers/functions/generateExcel";
import moment from "moment";
import {Box, useTheme, useMediaQuery, Paper, IconButton} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";

function Transaction() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const [isFilterOpen, setIsFilterOpen] = useState(!isMobile);

  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [isSpinner, setIsSpinner] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [alertType, setAlertType] = useState("");
  const [message, setMessage] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecord, setTotalRecord] = useState(0);
  // ==================
  const [orderBy, setOrderBy] = useState("");
  const [order, setOrder] = useState("desc");
  // ==================
  const [salesList, setSalesList] = useState(null);
  // ==================
  const [branchList, setBranchList] = useState([]);
  const [searchBranchName, setSearchBranchName] = useState("");
  const [branchCodeSelected, setBranchCodeSelected] = useState("");
  // ==================
  const [selectedDateFrom, setSelectedDateFrom] = useState(null);
  const [selectedDateTo, setSelectedDateTo] = useState(null);
  const [transactionCode, setTransactionType] = useState(20);

  // ===============
  const onGetSales = async () => {
    setIsLoading(true);
    const currentPage = page + 1;
    const dateFromSelected =
      selectedDateFrom === null
        ? ""
        : moment(selectedDateFrom).format("YYYY-MM-DD");
    const dateToSelected =
      selectedDateTo === null
        ? ""
        : moment(selectedDateTo).format("YYYY-MM-DD");
    try {
      const res = await api
        .getTransactionData(
          branchCodeSelected,
          transactionCode,
          dateFromSelected,
          dateToSelected,
          rowsPerPage,
          currentPage
        )
        .then(async (res) => {
          if (res.statusCode === 200) {
            setTotalRecord(res.data.totalPage * res.data.limit);
            setSalesList(res.data.data);
            setIsLoading(false);
          } else {
            setSalesList([]);
            onError(res.message);
          }
        })
        .catch((error) => {
          setSalesList([]);
          onError(error.message);
        });
    } catch (error) {
      setSalesList([]);
      onError(error.message);
    }
  };

  const onGetBranches = async () => {
    try {
      const res = await api
        .getBranch(searchBranchName)
        .then(async (res) => {
          if (res.statusCode === 200) {
            const newBranch = {
              id: "",
              branchName: "All Branches",
              address: "",
              branchPhone: "",
              statusName: "",
              statusCode: "",
              branchCode: "",
              createdDate: "",
              updatedDate: "",
            };
            const updatedBranchList = [newBranch, ...res.data.data];
            setBranchList(updatedBranchList);
          } else {
            onError(res.message);
          }
        })
        .catch((error) => {
          onError(error.message);
        });
    } catch (error) {
      onError(error.message);
    }
  };

  const onHandleReport = async () => {
    setIsSpinner(true);
    const dateFromSelected =
      selectedDateFrom === null
        ? ""
        : moment(selectedDateFrom).format("YYYY-MM-DD");
    const dateToSelected =
      selectedDateTo === null
        ? ""
        : moment(selectedDateTo).format("YYYY-MM-DD");
    try {
      const res = await api
        .reportDownload(transactionCode, dateFromSelected, dateToSelected)
        .then(async (res) => {
          if (res.statusCode === 200) {
            const fileName = "sales-report";
            setIsSpinner(false);
            exportToXLSX(res.data.data, fileName);
            onSuccessSubmit();
          } else {
            onError(res.message);
          }
        })
        .catch((error) => {
          onError(error.message);
        });
    } catch (error) {
      onError(error.message);
    }
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    const newOrder = isAsc ? "desc" : "asc";

    const sortedData = [...salesList].sort((a, b) => {
      // Use dynamic property access with bracket notation
      const valueA = a[property];
      const valueB = b[property];

      // Handle different types of sorting
      if (typeof valueA === "string") {
        return newOrder === "asc"
          ? valueA.localeCompare(valueB)
          : valueB.localeCompare(valueA);
      }

      return newOrder === "asc" ? valueA - valueB : valueB - valueA;
    });

    setSalesList(sortedData);
    setOrder(newOrder);
    setOrderBy(property);
  };

  // ==============
  useEffect(() => {
    const fetch = async () => {
      await onGetSales();
    };
    fetch();
  }, [page, rowsPerPage]);

  useEffect(() => {
    onGetBranches();
  }, [searchBranchName]);

  const onError = async (message) => {
    setIsLoading(false);
    setIsSpinner(false);
    setIsOpen(true);
    setAlertType("error");
    setMessage(message);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const onSuccessSubmit = async () => {
    setIsOpen(true);
    setAlertType("success");
    setMessage("Successfully...");
  };

  const handlePreviewDetail = (itemSelected) => {
    // Navigasi ke halaman detail dengan ID transaksi dan mengirim data transaksi melalui state
    navigate(`/transaction/detail/${itemSelected.id}`, {
      state: {transaction: itemSelected},
    });
  };

  const handleSearch = () => {
    setPage(0);
    onGetSales();
    if (isMobile) {
      setIsFilterOpen(false);
    }
  };

  return (
    <>
      <Spinner isShowSpinner={isSpinner} />
      <CustomizedSnackbars
        isOpen={isOpen}
        typeInfo={alertType}
        messageInfo={message}
        handleCloseSnackbar={() => setIsOpen(false)}
      />

      <Paper
        elevation={1}
        sx={{
          width: "100%",
          borderRadius: 1,
          overflow: "hidden",
          mb: 3,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            flexDirection: {xs: "column", sm: "row"},
            justifyContent: "space-between",
            alignItems: {xs: "flex-start", sm: "center"},
            p: {xs: 2, sm: 3},
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <Typography
              variant={isMobile ? "subtitle1" : "h6"}
              sx={{
                fontWeight: "medium",
                width: {xs: "80%", sm: "auto"},
              }}
            >
              Sales List
            </Typography>

            {isMobile && (
              <IconButton
                color="primary"
                onClick={() => setIsFilterOpen(!isFilterOpen)}
                size="small"
              >
                <FilterListIcon />
              </IconButton>
            )}
          </Box>

          {(!isMobile || !isFilterOpen) && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<DownloadForOfflineIcon />}
              onClick={onHandleReport}
              size={isMobile ? "small" : "medium"}
              sx={{
                mt: {xs: 2, sm: 0},
                ml: {sm: 2},
                whiteSpace: "nowrap",
                minWidth: {xs: "auto", sm: "160px"},
                px: {xs: 1.5, sm: 2},
                fontSize: {xs: "0.8125rem", sm: "0.875rem"},
              }}
            >
              Download Report
            </Button>
          )}
        </Box>

        {/* Search Section */}
        {(isFilterOpen || !isMobile) && (
          <Box
            sx={{
              p: {xs: 2, sm: 3},
              borderBottom: "1px solid",
              borderColor: "divider",
              position: "relative",
            }}
          >
            {isMobile && (
              <Box sx={{position: "absolute", top: 10, right: 10, zIndex: 5}}>
                <IconButton
                  size="small"
                  onClick={() => setIsFilterOpen(false)}
                  color="default"
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            )}

            <Grid
              container
              spacing={2}
              alignItems="flex-start"
              sx={{
                "& .MuiGrid-item": {
                  display: "flex",
                  flexDirection: "column",
                },
              }}
            >
              <Grid item xs={12} sm={6} md={3}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    fontSize: isMobile ? "0.7rem" : "0.75rem",
                    mb: 1,
                  }}
                >
                  CHOOSE OUTLET
                </Typography>
                <PickerSearch
                  dataList={branchList}
                  onInputChange={(event, newInputValue) => {
                    setSearchBranchName(newInputValue);
                  }}
                  onChange={(e, v) => {
                    if (v) {
                      setBranchCodeSelected(v.branchCode);
                    } else {
                      setBranchCodeSelected("");
                    }
                  }}
                  getOptionLabel={(branchList) => branchList.branchName || ""}
                  labelPicker="Choose Outlet"
                  placeholderPicker="search outlet"
                  sizePicker="small"
                  isVisibleLabelPicker={false}
                  sx={{width: "100%"}}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    fontSize: isMobile ? "0.7rem" : "0.75rem",
                    mb: 1,
                  }}
                >
                  DATE FROM
                </Typography>
                <FormDatePicker
                  labelFormDatePicker="Date From"
                  dateValue={selectedDateFrom}
                  onChangeDate={(val) => setSelectedDateFrom(val)}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    fontSize: isMobile ? "0.7rem" : "0.75rem",
                    mb: 1,
                  }}
                >
                  DATE TO
                </Typography>
                <FormDatePicker
                  labelFormDatePicker="Date To"
                  dateValue={selectedDateTo}
                  onChangeDate={(val) => setSelectedDateTo(val)}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    fontSize: isMobile ? "0.7rem" : "0.75rem",
                    mb: 1,
                    opacity: 0,
                    display: {xs: "none", sm: "block"},
                  }}
                >
                  &nbsp;
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: {xs: "center", sm: "flex-start"},
                    width: "100%",
                  }}
                >
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<SearchIcon />}
                    onClick={handleSearch}
                    sx={{
                      height: 40,
                      mt: {xs: 1, sm: 0},
                      width: {
                        xs: "100%",
                        sm: "100%",
                        md: "auto",
                      },
                      maxWidth: {xs: "none", sm: "none", md: "160px"},
                      minWidth: {xs: "none", sm: "none", md: "120px"},
                    }}
                  >
                    Search
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}

        {/* Table List */}
        <Box
          sx={{
            overflowX: "auto",
            "& .MuiTableCell-root": {
              whiteSpace: isMobile ? "nowrap" : "normal",
              px: {xs: 1, sm: 2},
              py: {xs: 1, sm: 1.5},
            },
          }}
        >
          <TableList
            tableStructure="sales"
            tableName={"Sales"}
            orderBy={orderBy}
            order={order}
            isLoading={isLoading}
            isShowPagination={true}
            tableHeadList={tableHeadTransaction}
            data={salesList}
            rowsPerPage={rowsPerPage}
            page={page}
            totalRecord={totalRecord}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            onPreviewDetail={handlePreviewDetail}
            handleRequestSort={handleRequestSort}
          />
        </Box>
      </Paper>
    </>
  );
}

export default Transaction;
